import { useEffect, useMemo, useRef } from 'react';
import echo from '../echo';

export const DRIVER_LOCATION_UPDATED_EVENT =
  '.App\\Events\\DriverLocationUpdated';

export const getDriverLocationChannel = (driverId) =>
  `np-driver-locations-${driverId}`;

const removeSocketListener = (socket, event, listener) => {
  if (typeof socket?.off === 'function') {
    socket.off(event, listener);
    return;
  }

  socket?.removeListener?.(event, listener);
};

export const useDriverLocationRealtime = ({
  driverIds = [],
  enabled = true,
  onLocation,
  onReconnect,
  onConnectionChange,
} = {}) => {
  const handlersRef = useRef({ onLocation, onReconnect, onConnectionChange });
  handlersRef.current = { onLocation, onReconnect, onConnectionChange };

  const driverKey = useMemo(
    () =>
      Array.from(new Set(driverIds.map(String)))
        .sort()
        .join(','),
    [driverIds],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const ids = driverKey ? driverKey.split(',') : [];
    const channels = ids.map((driverId) => {
      const channelName = getDriverLocationChannel(driverId);
      const channel = echo.channel(channelName);

      channel.listen(DRIVER_LOCATION_UPDATED_EVENT, (event) => {
        if (String(event?.driver_id) === driverId) {
          handlersRef.current.onLocation?.(event);
        }
      });

      return channelName;
    });

    const socket = echo?.connector?.socket;
    const handleConnect = () => {
      handlersRef.current.onConnectionChange?.(true);
      handlersRef.current.onReconnect?.();
    };
    const handleDisconnect = () =>
      handlersRef.current.onConnectionChange?.(false);

    handlersRef.current.onConnectionChange?.(Boolean(socket?.connected));
    socket?.on?.('connect', handleConnect);
    socket?.on?.('disconnect', handleDisconnect);
    socket?.on?.('connect_error', handleDisconnect);

    return () => {
      removeSocketListener(socket, 'connect', handleConnect);
      removeSocketListener(socket, 'disconnect', handleDisconnect);
      removeSocketListener(socket, 'connect_error', handleDisconnect);
      channels.forEach((channelName) => echo.leave(channelName));
    };
  }, [driverKey, enabled]);
};
